// Band-strategy signal math shared by the Bollinger chart backtest
// (src/components/PriceBollingerChart.jsx) and the signal-watch-cron worker,
// which advances the same state machine server-side for alerts. One source of
// truth — the worker bundles this file via a relative import, so neither side
// keeps a private copy that can drift.
import { STRATEGIES } from '../constants/signalStrategies.js';

// SMA periods the fit ranks over. 100D was removed (issue #66) — too slow to
// complete trades in any window; fp_user still ACCEPTS period=100 so
// pre-existing watches keep working.
export const SMA_PERIODS = [3, 5, 10, 20, 50];

// Calendar days of daily bars a fit is ranked over: 1Y of backtest window plus
// the longest SMA's warmup. The chart fetches this much, and so does the
// server refit, so both rank the identical series.
export const FIT_HISTORY_DAYS = 550;

// Band width scales with the period, Bollinger-style: fast bands need tight
// multipliers to generate signals at all (a 3-5 day window at 2σ nearly
// always contains its closes), slow bands need wide ones to avoid noise
export const sigmaForPeriod = (period) => (period >= 50 ? 2 : period >= 10 ? 1.5 : 1);

// Entry/exit zone: within this fraction of band width from a band edge
export const TOUCH_MARGIN = 0.05;

// Strategy-key predicates. The four strategies form a 2×2 (entry side × exit
// type): band = long/band-exit, meanLong = long/mean-exit, meanShort =
// short/mean-exit, shortBand = short/band-exit. Every consumer (chart backtest,
// signal-watch-cron, fp_user) must classify keys through these, not by
// comparing against 'band'/'meanShort' literals that miss the new quadrant.
export const isShortStrategy = (strategy) => strategy === 'meanShort' || strategy === 'shortBand';
export const usesBandExit = (strategy) => strategy === 'band' || strategy === 'shortBand';

// Rolling SMA + Bollinger bands over ascending-by-date rows with {close}.
// Returns a parallel array of {...row, sma, upper, lower} (null during warmup).
// SD window follows the SMA period, and the multiplier is derived from it too —
// call sites can't pair them wrongly.
export const computeBandsSeries = (rows, period) => {
  const sigma = sigmaForPeriod(period);
  return rows.map((row, i) => {
    if (i < period - 1) return { ...row, sma: null, upper: null, lower: null };
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += rows[j].close;
    const mean = sum / period;
    let variance = 0;
    for (let j = i - period + 1; j <= i; j++) variance += (rows[j].close - mean) ** 2;
    const sd = Math.sqrt(variance / period);
    return { ...row, sma: mean, upper: mean + sigma * sd, lower: mean - sigma * sd };
  });
};

// ENTRY tests the SAME-day band: the band the chart draws is the band that
// signals, so touches register the day they happen. A 45-ticker/1Y backtest
// (2026-07-17) showed same-day entries capture ~9% more ≥2% swings and +16%
// cumulative return at an unchanged win rate vs the old prior-day rule.
export const entrySignal = (p, isShort) => {
  if (p.upper == null) return false;
  const margin = (p.upper - p.lower) * TOUCH_MARGIN;
  return isShort ? p.close >= p.upper - margin : p.close <= p.lower + margin;
};

// band-strategy EXIT keeps the PRIOR-day band: in an uptrend yesterday's upper
// band sits lower, locking profits earlier. The same backtest showed same-day
// exits add churn (−4pts win rate, halved median) for negligible extra capture.
// Shorts mirror it — cover in the prior-day lower-band zone.
export const bandExitSignal = (p, prev, isShort) => {
  if (prev?.upper == null) return false;
  const margin = (prev.upper - prev.lower) * TOUCH_MARGIN;
  return isShort ? p.close <= prev.lower + margin : p.close >= prev.upper - margin;
};

// Mean strategies exit at the SMA FROZEN at entry — but the reversion thesis
// has a shelf life. A frozen target with no stop never realizes losses: the
// 45-ticker backtest left 212 zombie positions (−4,289% unrealized) that also
// blocked every re-entry. Force-closing at market after 2× the SMA period
// nearly doubled swings captured (791→1,471 ≥2%) and cut stuck capital 86%.
// K=2 vs 1.5 was a statistical tie (per-ticker 20/23 split) — 2 keeps the
// median trade meatier. Demoting the target to the current SMA at timeout
// instead of closing lost in every test window: a falling mean keeps sliding
// away ahead of price, so you exit later, no better, and stay blocked.
export const MEAN_TIME_STOP_K = 2;

// barsHeld = trading bars since entry. Callers that can't locate the entry
// bar in their loaded window should pass Infinity (held longer than any
// lookback ⇒ certainly past the stop).
export const meanExitSignal = (p, frozenTarget, barsHeld, period, isShort) => {
  if (barsHeld >= MEAN_TIME_STOP_K * period) return true;
  return isShort ? p.close <= frozenTarget : p.close >= frozenTarget;
};

// ADVERSE-PRICE STOP (issue #141). Every other exit in this file points toward
// profit — band exits at the OPPOSITE band, mean exits at the frozen target —
// so a losing position is bounded only by the mean time stop's clock, and band
// strategies are not bounded at all. TSLA meanLong p3 rode to -21.67% before
// its clock matured, while the intraday scans evaluated it twice a session and
// correctly did nothing: no rule tests an adverse move.
//
// Measured in entry-day standard deviations, deliberately NOT a fixed percent
// and NOT a fraction of band width. Band width is 2*sigmaForPeriod(period)*sd,
// and sigmaForPeriod varies 1 -> 1.5 -> 2 across the period ladder, so one
// "band width" is a different volatility distance at period 5 than at period
// 50. sd is the raw unit, which makes K mean the same thing everywhere.
//
// sd is captured AT ENTRY and frozen. A stop recomputed from the live band
// would widen as volatility rises — i.e. loosen exactly when a position is
// going wrong — which is the opposite of what a stop is for.
// K = 2.0 entry-day standard deviations, fitted from a 56-ticker study over TWO
// INDEPENDENT 252-bar years (4 quadrants x 5 periods, ~15.5k baseline trades per
// year). K=2.0 was the peak for BOTH cumulative return and return-per-bar-in-
// market in BOTH years — the agreement across independent windows is what makes
// it a fit rather than a curve-fit.
//
//   year 1   off -> K=2.0 : trades 15796->21715, <-20% losses 395->168,
//                           1st pctile -29.9%->-18.7%, ret/bar 6.1->7.6 bps
//   year 2   off -> K=2.0 : trades 15224->20926, <-20% losses 424->247,
//                           1st pctile -31.8%->-20.9%, ret/bar 6.0->7.2 bps
//
// Win rate falls 65% -> 54% and the median trade 1.8% -> 1.0%. That is what a
// stop DOES: it converts some would-be winners into small losses to cut the
// tail. Both stay positive at K=2.0; at K<=1.25 the median goes NEGATIVE and
// return-per-bar falls, i.e. the stop becomes pure churn. Above K=2.5 too much
// tail survives.
//
// Judge changes here by return-per-bar-in-market and tail counts, never by win
// rate or cumulative-return-per-trade — an exit rule that never stops out shows
// a great win rate while carrying unbounded losses in still-open positions.
//
// Study caveat: the first pass at this used our own historical_prices and found
// K=1.5-1.75, because coverage gaps (issue #143) left only 21 of 60 tickers with
// a usable year. Re-run on authoritative adjusted closes it moved to 2.0. Re-fit
// from complete data, and re-derive after a regime change rather than nudging K
// between studies.
export const ADVERSE_STOP_K = 2.0;

// Per-strategy stop (issue #417): the σ multiple a strategy's book stops out at,
// chosen on its strategy card; ADVERSE_STOP_K above is the fitted DEFAULT, no
// longer the only value. 0 = off. The ladder bottoms out at 1.5 on purpose: at
// K <= 1.25 the study above found the median trade turns negative — pure churn.
export const STOP_K_OPTIONS = [0, 1.5, 2, 2.5, 3];

// strategies.stop_k is stored raw and resolved here, like resolvePositionCap:
// NULL (never set) → the fitted default; 0 → off; anything off the ladder → the
// default, so a corrupt value can never silently DISABLE the stop. The null
// check comes before Number() because Number(null) is 0 — "never set" must not
// read as "off".
export const resolveStopK = (stored) => {
  if (stored == null || stored === '') return ADVERSE_STOP_K;
  const n = Number(stored);
  return STOP_K_OPTIONS.includes(n) ? n : ADVERSE_STOP_K;
};

export const sdAtEntry = (p, period) => {
  if (p.upper == null || p.sma == null) return null;
  const sd = (p.upper - p.sma) / sigmaForPeriod(period);
  return sd > 0 ? sd : null;
};

// k = null/0 disables the stop (used by the sweep to measure the baseline).
export const adverseExitSignal = (p, entryPrice, sd, k, isShort) => {
  if (!k || sd == null) return false;
  return isShort ? p.close >= entryPrice + k * sd : p.close <= entryPrice - k * sd;
};

// Why a closed position closed, from the same inputs on both sides: the
// backtest labelling a historical exit and signal-watch-cron labelling a live
// one are answering the identical question, so they must never disagree — a
// census of exit reasons from a re-fit study is only comparable to production's
// emitted reasons if one rule produces both. (Contrast isMarketFresh /
// pickLiveQuote, which look alike but are REQUIRED to diverge; these are not.)
//
// Ordered deliberately. Adverse first: it is the only exit that fires on a move
// AGAINST the position, so when it triggers it is always the reason worth
// recording. Then 'band', which has no other candidate. For mean strategies
// 'target' wins when the goal and the clock land on the same bar — price
// reached the goal, so the clock is moot.
//
// Callers pass the bar's close and the position's FROZEN target (null for band
// strategies, where it is unreachable behind the usesBandExit branch anyway).
// 'window_end' is not derivable here: it is an accounting artifact of a
// backtest running out of bars, not a rule firing, so that caller passes it in.
export const exitReason = (strategy, { adverse, close, frozenTarget, isShort }) => {
  if (adverse) return 'adverse';
  if (usesBandExit(strategy)) return 'band';
  const targetHit = frozenTarget != null && (isShort ? close <= frozenTarget : close >= frozenTarget);
  return targetHit ? 'target' : 'time_stop';
};

// One-position backtest of a strategy over the last rangeDays bars of a
// banded series (computeBandsSeries output). Used by the Trading Range
// chart's fit scoring AND the notifications dialog's per-watch stats — one
// implementation so the numbers always agree. Returns cumulative trade stats
// plus the entry/exit event list and, for mean strategies, the frozen-target
// spans the chart draws. An open position at window end closes at the last
// bar, so buying dips in a downtrend honestly shows its losses — but that
// close-out is an accounting artifact, not a signal, so it emits no event
// (the dangling entry event reads as "still holding").
export const backtestStrategy = (series, strategy, period, rangeDays, { adverseK = ADVERSE_STOP_K } = {}) => {
  const short = isShortStrategy(strategy);
  const start = Math.max(1, series.length - rangeDays);
  let entry = null; // { price, target, date, entryIdx } — target/entryIdx only used by mean strategies
  let trades = 0;
  let wins = 0;
  let totalReturn = 0;
  let barsInMarket = 0;
  const returns = [];
  const exitReasons = {};
  const events = [];
  const spans = [];
  const closeTrade = (exitClose, exitDate, exitIdx, reason) => {
    const r = short ? entry.price / exitClose - 1 : exitClose / entry.price - 1;
    totalReturn += r;
    trades++;
    if (r > 0) wins++;
    barsInMarket += exitIdx - entry.entryIdx;
    returns.push(r);
    if (reason) exitReasons[reason] = (exitReasons[reason] || 0) + 1;
    if (!usesBandExit(strategy)) spans.push({ from: entry.date, to: exitDate, target: entry.target });
    entry = null;
  };
  for (let i = start; i < series.length; i++) {
    const p = series[i];
    const prev = series[i - 1];
    if (entry == null) {
      if (entrySignal(p, short)) {
        entry = { price: p.close, target: p.sma, date: p.date, entryIdx: i, sd: sdAtEntry(p, period) };
        events.push({ date: p.date, type: short ? 'sell' : 'buy' });
      }
    } else {
      // Evaluated once and reused as both the trigger and the label — testing
      // the same predicate twice invites the two copies to drift apart.
      const adverse = adverseExitSignal(p, entry.price, entry.sd, adverseK, short);
      const exited = adverse || (usesBandExit(strategy) ? bandExitSignal(p, prev, short)
        : meanExitSignal(p, entry.target, i - entry.entryIdx, period, short));
      if (exited) {
        const reason = exitReason(strategy, { adverse, close: p.close, frozenTarget: entry.target, isShort: short });
        closeTrade(p.close, p.date, i, reason);
        events.push({ date: p.date, type: short ? 'buy' : 'sell' });
      }
    }
  }
  if (entry != null) {
    const last = series[series.length - 1];
    closeTrade(last.close, last.date, series.length - 1, 'window_end');
  }
  return { trades, wins, totalReturn, events, spans, barsInMarket, returns, exitReasons };
};

// ---- Fit ranking: the chart's Auto, shared with the server refit (issue #472) ----

// A period must complete this many round trips in the window to be ranked
export const MIN_FIT_TRADES = 2;

// Backtest one shape at every period over the trailing rangeDays bars.
// score = cumulative trade return; below MIN_FIT_TRADES a period is reported
// (silence reads as "broken" rather than "few signals") but never ranked.
// stopK = the strategy's stop (issue #417), so the scored trades are the ones
// the scans would take. Returns { stats: {period: result+eligible}, best }.
export const fitStatsFor = (prices, rangeDays, strategy, stopK) => {
  const stats = {};
  let best = null;
  for (const period of SMA_PERIODS) {
    const result = backtestStrategy(computeBandsSeries(prices, period), strategy, period, rangeDays, { adverseK: stopK });
    const eligible = result.trades >= MIN_FIT_TRADES;
    stats[period] = { ...result, eligible };
    if (eligible && (best == null || result.totalReturn > stats[best].totalReturn)) best = period;
  }
  return { stats, best };
};

// Auto: every shape at its own best period, then shapes compete on score.
// First-wins on ties (STRATEGIES order, then ascending period). `shapes`
// narrows the field — the server passes long shapes only for a book with
// shorts off, where a short winner could never trade. `period` pins the SMA
// period instead (the chart's Auto shape with a manual period): shapes then
// compete AT that period. Returns { strategy, period, score, fit } or null
// when nothing is rankable.
export const autoFit = (prices, rangeDays, stopK, shapes = STRATEGIES.map((s) => s.key), period = null) => {
  let winner = null;
  for (const key of shapes) {
    const fit = fitStatsFor(prices, rangeDays, key, stopK);
    const p = period ?? fit.best;
    const s = p != null ? fit.stats[p] : null;
    if (s?.eligible && (winner == null || s.totalReturn > winner.score)) {
      winner = { strategy: key, period: p, score: s.totalReturn, fit };
    }
  }
  return winner;
};

// The chart's whole fit resolution, as one pure function: a pinned shape scores
// its periods; Auto with period-Auto is autoFit; Auto with a manual period has
// shapes compete AT that period. Nothing rankable → the default shape's stats.
// Returns { stats, best, resolvedStrategy }; resolvedStrategy is what
// everything downstream (markers, summary, saved watches) uses.
export const resolveChartFit = (prices, rangeDays, { auto, autoPeriod, strategy, smaPeriod, stopK, defaultStrategy }) => {
  if (!auto) return { ...fitStatsFor(prices, rangeDays, strategy, stopK), resolvedStrategy: strategy };
  const winner = autoFit(prices, rangeDays, stopK, undefined, autoPeriod ? null : smaPeriod);
  if (winner) return { ...winner.fit, resolvedStrategy: winner.strategy };
  return { ...fitStatsFor(prices, rangeDays, defaultStrategy, stopK), resolvedStrategy: defaultStrategy };
};

// PERIODIC REFIT of flat watches (issue #472). The refit study (#114,
// scripts/research/REFIT_STUDY.md) found re-ranking more often than the fit's
// own scoring window loses — monthly refits lost in 5 of 6 test windows — and
// that a switch needs a margin over the incumbent or the shape chases noise.
// Cadence = the watch's lookback (its scoring window), never less than 126 bars;
// margin = 0.15 cumulative return (15 points) over the incumbent, both scored
// over the same window. Only FLAT watches refit: a refit that touches an open
// position either sells it at market or re-aims its target, and the 2026-10-19
// timeout study measured re-aiming as no better than selling (#472).
export const REFIT_MIN_CADENCE_BARS = 126;
export const REFIT_MARGIN = 0.15;
export const refitCadenceBars = (lookbackDays) => Math.max(REFIT_MIN_CADENCE_BARS, Number(lookbackDays) || 0);

// The refit decision for one flat watch: { switch, challenger, incumbentScore }.
// Keeps the incumbent when nothing is rankable, when the winner IS the
// incumbent, or when it doesn't clear the margin. The incumbent is scored even
// if it fell below MIN_FIT_TRADES — "it barely traded" is a real (low) score.
// An incumbent outside `shapes` (a short fit on a book with shorts off) can
// never trade, so its paper score must not protect it: any rankable
// challenger replaces it.
export const refitDecision = (prices, rangeDays, stopK, incumbent, shapes) => {
  const challenger = autoFit(prices, rangeDays, stopK, shapes);
  const inc = backtestStrategy(computeBandsSeries(prices, incumbent.period), incumbent.strategy, incumbent.period, rangeDays, { adverseK: stopK });
  const same = challenger && challenger.strategy === incumbent.strategy && challenger.period === incumbent.period;
  const tradable = !shapes || shapes.includes(incumbent.strategy);
  const switchFit = !!challenger && !same && (!tradable || challenger.score > inc.totalReturn + REFIT_MARGIN);
  return { switch: switchFit, challenger, incumbentScore: inc.totalReturn };
};

// EARNINGS BLACKOUT (issue #113). An earnings gap is the one loss the exit
// machinery provably cannot touch: the four durable scans span 09:20-15:30 ET,
// an AMC release lands at 16:00 ET, and the reaction prints at the next open.
// The adverse stop (ADVERSE_STOP_K) is evaluated on closes and the move happens
// between two of them. Only NOT being in the position helps.
//
// Window = the same 2x period the mean time stop uses, applied to ALL four
// quadrants: don't enter if a confirmed report falls inside the span you could
// still be holding. Measured over 56 tickers x 2 years (41,839 trades, on the
// subset with known earnings dates):
//
//   held through earnings : win 48.4%, mean -0.28%, median -0.72%, 22.0% lost >10%
//   did not              : win 54.2%, mean +0.42%, median +0.99%,  6.7% lost >10%
//
// Holding through a print flips the edge negative and TRIPLES the rate of >10%
// losses. Rule comparison, by big-losses-removed per trade sacrificed:
//
//   flat 5 sessions   cut 1008 trades, 132 big losses  13.1%  1.84x enrichment
//   flat 10 sessions  cut 1960 trades, 211 big losses  10.8%  1.52x
//   2 x period        cut 2830 trades, 453 big losses  16.0%  2.25x  <- best
//
// A FLAT window loses precision as it widens (2.86x at 1 session -> 1.52x at 10)
// because it is simultaneously too wide for fast strategies and too narrow for
// slow ones. Scaling with period fixes both ends. It also fits band strategies,
// which have no time stop: measured band hold length is ~p95 at 2x period for
// every period (3->6, 5->9, 10->18, 20->33, 50->78), so one formula covers all
// four quadrants with no special case.
//
// Caveat on the study: earnings_history covers only ~40% of the basket, so the
// control group contains unknown earnings spans. That biases toward finding NO
// difference — the true effect is larger than measured, not smaller.
export const blackoutBars = (period) => MEAN_TIME_STOP_K * period;

// Bars from `fromDate` to the earnings session, or null when there is no
// confirmed date. Callers pass ESTIMATED dates as null: our own
// estimateNextEarningsDate() fallback manufactures times that mean nothing, and
// suppressing a real signal on a fabricated date is a silent loss of edge.
export const barsUntilEarnings = (series, fromIdx, earningsDate) => {
  if (!earningsDate || fromIdx == null || fromIdx < 0 || !series.length) return null;
  const day = String(earningsDate).slice(0, 10);
  const idx = series.findIndex((r) => r.date >= day);
  if (idx !== -1) return idx - fromIdx;      // the report sits inside the series

  // Beyond the last loaded bar. This is the NORMAL case for a live scan — the
  // next confirmed report is always in the future — so returning null here made
  // the blackout a no-op in production while still passing any test whose
  // fixture happened to contain the date. Count trading days forward from the
  // last bar instead. Weekend-only approximation: it ignores market holidays,
  // so it can under-count by at most a day or two per quarter, which widens the
  // blackout slightly rather than leaving a gap — the safe direction.
  const last = series[series.length - 1].date;
  if (day <= last) return null;
  let d = new Date(`${last}T00:00:00Z`);
  const target = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime()) || Number.isNaN(target.getTime())) return null;
  let sessions = 0;
  while (d < target) {
    d = new Date(d.getTime() + 86400000);
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) sessions++;
  }
  return (series.length - 1 - fromIdx) + sessions;
};

// True when an entry at fromIdx would still be open across a confirmed report.
export const inEarningsBlackout = (series, fromIdx, earningsDate, period) => {
  const bars = barsUntilEarnings(series, fromIdx, earningsDate);
  return bars != null && bars >= 0 && bars <= blackoutBars(period);
};

// PORTFOLIO CAP (issue #154). Watches are independent one-position state
// machines, so nothing bounded how many could be open at once — real capital is
// finite and the system did not model that.
//
// When the cap binds, the contested slot goes to the candidate with the highest
// ENTRY VOLATILITY (sigma as % of price). Measured over 56 tickers x 2 years,
// 41,835 trades, bucketed by entry sigma:
//
//   Q1 (<=0.85%)  mean 0.06%/trade   3.3 bps per bar   0.4% lost >10%
//   Q5 (>3.64%)   mean 1.42%/trade  11.6 bps per bar  26.6% lost >10%
//
// Win rate is FLAT across every quintile (52.5-55.5%) — volatility does not
// improve the hit rate, it scales the SIZE of both outcomes. Return-per-bar
// rises only because ADVERSE_STOP_K caps the downside at 2 sigma while winners
// run to the full band. THE TILT IS ONLY SAFE BECAUSE THAT STOP EXISTS.
//
// Rank by the band, not by a fine-grained score: across two independent years
// only Q5 replicated (13.1 and 9.9 bps, top in both), while Q2/Q3 swung
// 0.5 -> 7.1 bps. The middle of the gradient is noise.
//
// Rejected: a LOW-BETA tiebreaker. Intuition says low beta diversifies a
// concurrent book, and a capped-book simulation across cap {3,5,10} x 2 years
// says otherwise — low beta alone was the WORST rule tested (return/drawdown
// floor 0.13) because filtering for it starves the candidate pool. The
// hi-vol/lo-beta blend had the best median but a 0.30 floor and 4x the spread
// of any other rule; it looked 50% ahead in ONE configuration and did not
// replicate. `prefer HIGH vol` was the only rule to beat first-come in all six.
// DEFAULT only — the effective cap is per STRATEGY and passed in (strategies.position_cap, #322).
// Kept as the fallback so a missing/invalid setting cannot silently mean "unlimited".
export const DEFAULT_MAX_OPEN_POSITIONS = 5;

// Resolve a stored per-user cap to an effective one. A null/absent value means
// "not configured" and takes the default; anything non-positive or non-finite is
// treated as unset rather than as "no positions allowed", so a bad row can never
// silently freeze a user's book.
export const resolvePositionCap = (stored) => {
  const n = Number(stored);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : DEFAULT_MAX_OPEN_POSITIONS;
};

// Entry-time volatility as a percentage of price — the slot-contention score.
// Normalised by price so a $400 stock and a $4 stock compare on equal terms,
// and derived from the LIVE candidate cross-section rather than a hardcoded
// sigma%: a literal threshold would select nothing in a calm regime and
// everything in a violent one.
export const entryVolPct = (p, period) => {
  const sd = sdAtEntry(p, period);
  if (sd == null || !p || !(p.close > 0)) return null;
  return (sd / p.close) * 100;
};

// Position-snapshot math shared by signal-watch-cron's webhook digest
// (buildPosition) and fp_user's signals pull API (buildSignalsPayload) — both
// render the same open-position shape, one from an in-memory series, the
// other from a D1 row, so only the math (not the data access) lives here.
export const unrealizedPlPct = (entryPrice, lastClose, isShort) => {
  if (lastClose == null || !entryPrice) return null;
  const pct = ((isShort ? entryPrice - lastClose : lastClose - entryPrice) / entryPrice) * 100;
  return Number(pct.toFixed(2));
};

export const timeStopBars = (strategy, period) => (usesBandExit(strategy) ? null : MEAN_TIME_STOP_K * period);

// The price the adverse stop closes at — frozen_target's counterpart on the
// loss side. Both surfaces render the SAME level for the SAME position, so the
// arithmetic belongs here even though each obtains sd differently (buildPosition
// from the series it already holds, buildSignalsPayload from a fetched window);
// only the math is shared, never the data access.
//
// k is the position's STRATEGY stop (resolveStopK of its strategies row, issue
// #417) — the same value the scan passes to adverseExitSignal, because a
// displayed level computed with any other k would be a lie. It has no default
// so a caller that forgets it reports null rather than a confident 2σ level.
//
// null means no level to show: sd == null (the entry bar fell outside the
// caller's window — UNKNOWN, never a guess from a nearby bar) or k = 0 (the
// strategy's stop is off — there is no level).
export const adverseStopPrice = (entryPrice, sd, k, isShort) => {
  if (!k || sd == null || entryPrice == null) return null;
  const level = isShort ? entryPrice + k * sd : entryPrice - k * sd;
  return Number(level.toFixed(4));
};
